import { useEffect, useState } from 'react';
import { useUserStore } from '@Auth/store';
import { UserRole } from '@domain/schemas/user-role';

import { MAX_QUERY_LENGTH, MIN_QUERY_LENGTH, SEARCH_DEBOUNCE_MS } from '../constants';
import { useCommandPaletteShortcut } from '../hooks/useCommandPaletteShortcut';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { useTrackRecentContract } from '../hooks/useTrackRecentContract';
import { useCommandPaletteStore } from '../store';
import type { SearchResultType, SearchSort } from '../types';
import { CommandPalette } from '../views/CommandPalette';

export const CommandPaletteController = () => {
  'use memo';

  const { isOpen, open, close } = useCommandPaletteStore(({ isOpen, open, close }) => ({ isOpen, open, close }));
  const user = useUserStore((state) => state.user);

  const isUserRole = user?.role === UserRole.User;

  const [query, setQuery] = useState('');
  const [type, setType] = useState<SearchResultType | undefined>(undefined);
  const [sort, setSort] = useState<SearchSort>('relevance');

  // an over-long query stays in the input but is never sent to the search
  const isQueryTooLong = query.trim().length > MAX_QUERY_LENGTH;
  const debouncedQuery = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS);
  const hasQuery = debouncedQuery.length >= MIN_QUERY_LENGTH;

  useCommandPaletteShortcut();
  useTrackRecentContract();

  // reset state when the palette closes
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setType(undefined);
      setSort('relevance');
    }
  }, [isOpen]);

  return (
    <CommandPalette
      isOpen={isOpen}
      query={query}
      debouncedQuery={debouncedQuery}
      type={type}
      sort={sort}
      hasQuery={hasQuery}
      error={isQueryTooLong ? 'Query is too long' : undefined}
      onOpenChange={(nextOpen) => (nextOpen ? open() : close())}
      onQueryChange={setQuery}
      onTypeChange={setType}
      onSortChange={setSort}
      isUserRole={isUserRole}
    />
  );
};
